import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { changeLanguage, type LangCode } from '@/i18n'

interface LanguageEntry {
  code: string
  flag: string
  name: string
  i18nKey: LangCode
}

const LANGUAGES: LanguageEntry[] = [
  { code: 'EN', flag: '🇬🇧', name: 'English',       i18nKey: 'en' },
  { code: 'AR', flag: '🇸🇦', name: 'العربية',        i18nKey: 'ar' },
  { code: 'ZH', flag: '🇨🇳', name: '中文',            i18nKey: 'zh' },
  { code: 'JA', flag: '🇯🇵', name: '日本語',          i18nKey: 'ja' },
  { code: 'TH', flag: '🇹🇭', name: 'ภาษาไทย',       i18nKey: 'th' },
  { code: 'VI', flag: '🇻🇳', name: 'Tiếng Việt',    i18nKey: 'vi' },
  { code: 'MS', flag: '🇲🇾', name: 'Bahasa Melayu', i18nKey: 'ms' },
  { code: 'RU', flag: '🇷🇺', name: 'Русский',        i18nKey: 'ru' },
  { code: 'ES', flag: '🇪🇸', name: 'Español',        i18nKey: 'es' },
  { code: 'PT', flag: '🇧🇷', name: 'Português',      i18nKey: 'pt' },
  { code: 'TR', flag: '🇹🇷', name: 'Türkçe',         i18nKey: 'tr' },
  { code: 'EL', flag: '🇬🇷', name: 'Ελληνικά',       i18nKey: 'el' },
]

export function LanguageSelector({ dark }: { dark?: boolean } = {}) {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [hoverTrigger, setHoverTrigger] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function select(key: LangCode) {
    await changeLanguage(key)
    setOpen(false)
  }

  const current = LANGUAGES.find((l) => l.i18nKey === i18n.language) ?? LANGUAGES[0]!

  const triggerBg = dark
    ? (hoverTrigger ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.07)')
    : (hoverTrigger ? '#f8fafc' : '#ffffff')
  const triggerBorder = dark
    ? (hoverTrigger || open ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)')
    : (hoverTrigger || open ? '#2B8FD4' : '#e2e8f0')
  const codeColor = dark ? '#ffffff' : '#0f172a'
  const chevronColor = dark ? 'rgba(255,255,255,0.4)' : '#94a3b8'

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHoverTrigger(true)}
        onMouseLeave={() => setHoverTrigger(false)}
        aria-label="Select language"
        aria-expanded={open}
        className="inline-flex items-center"
        style={{
          gap: 8,
          background: triggerBg,
          border: `1px solid ${triggerBorder}`,
          borderRadius: 100,
          padding: dark ? '5px 12px' : '6px 12px',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{current.flag}</span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: codeColor,
            letterSpacing: '0.3px',
          }}
        >
          {current.code}
        </span>
        <ChevronDown
          style={{
            width: 16,
            height: 16,
            color: chevronColor,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {open ? (
        <div
          className="vela-lang-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 240,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            padding: 6,
            boxShadow: '0 8px 30px rgba(0,0,0,0.10)',
            zIndex: 100,
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -6,
              right: 20,
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid #ffffff',
            }}
          />
          {LANGUAGES.map((lang) => {
            const active = i18n.language === lang.i18nKey
            return (
              <LanguageRow
                key={lang.i18nKey}
                lang={lang}
                active={active}
                onSelect={() => select(lang.i18nKey)}
              />
            )
          })}
          <style>{`
            .vela-lang-dropdown::-webkit-scrollbar { width: 4px; }
            .vela-lang-dropdown::-webkit-scrollbar-track { background: transparent; }
            .vela-lang-dropdown::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
          `}</style>
        </div>
      ) : null}
    </div>
  )
}

function LanguageRow({
  lang,
  active,
  onSelect,
}: {
  lang: LanguageEntry
  active: boolean
  onSelect: () => void
}) {
  const [hover, setHover] = useState(false)
  const bg = active ? '#EFF6FF' : hover ? '#f8fafc' : 'transparent'

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex w-full items-center text-left"
      style={{
        gap: 12,
        padding: '9px 14px',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'background 0.15s',
        margin: '1px 0',
        background: bg,
        border: 'none',
      }}
      role="option"
      aria-selected={active}
    >
      <span
        style={{
          width: 30,
          fontSize: 12,
          fontWeight: 700,
          color: active ? '#2B8FD4' : '#94a3b8',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          flexShrink: 0,
        }}
      >
        {lang.code}
      </span>
      <span
        className="flex items-center justify-center"
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: '#f1f5f9',
          overflow: 'hidden',
          flexShrink: 0,
          fontSize: 18,
          lineHeight: '28px',
          textAlign: 'center',
        }}
      >
        {lang.flag}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 14,
          fontWeight: active ? 600 : 500,
          color: '#0f172a',
        }}
      >
        {lang.name}
      </span>
      {active ? (
        <span
          style={{
            color: '#2B8FD4',
            fontSize: 14,
            marginLeft: 'auto',
            fontWeight: 700,
          }}
        >
          ✓
        </span>
      ) : null}
    </button>
  )
}
